import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError } from '@kira/checkpoint-shared-frontend';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import ContentWrapper from 'components/ContentWrapper/ContentWrapper';
import FormControls from 'components/FormControls/FormControls';
import Panel from 'components/Panel/Panel';
import SelectMenu, { SelectMenuOption } from 'components/SelectMenu/SelectMenu';
import useGetPracticeAssessmentDetails from 'hooks/useGetPracticeAssessmentDetails';
import useGetPracticeAssessments from 'hooks/useGetPracticeAssessments';
import useNotification from 'hooks/useNotification';
import useUpdateAssessmentPracticeAssessment from 'hooks/useUpdateAssessmentPracticeAssessment';
import { t } from 'locales/i18n';

enum FormFields {
  TYPE = 'type',
  PRACTICE_ASSESSMENT = 'practiceAssessment',
}

interface FormData {
  [FormFields.TYPE]: string;
  [FormFields.PRACTICE_ASSESSMENT]: string;
}

const PracticeAssessment = () => {
  const [types, setTypes] = useState<SelectMenuOption[]>([]);
  const [assessments, setAssessments] = useState<SelectMenuOption[]>([]);

  const { interviewUid = '' } = useParams();
  const { data: practiceAssessments, isLoading } =
    useGetPracticeAssessments(interviewUid);
  const {
    data: practiceAssessmentDetails,
    isLoading: isLoadingPracticeAssessmentDetails,
  } = useGetPracticeAssessmentDetails(interviewUid);
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { updateAssessmentPracticeAssessment, isLoading: isLoadingForm } =
    useUpdateAssessmentPracticeAssessment();
  const queryClient = useQueryClient();

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.TYPE]: yup.string(),
        [FormFields.PRACTICE_ASSESSMENT]: yup.string().when(FormFields.TYPE, {
          is: (val: string) => val !== '',
          then: yup.string().required(t('Practice selection is required')),
          otherwise: yup.string(),
        }),
      }),
    [FormFields.TYPE, FormFields.PRACTICE_ASSESSMENT]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    values: {
      [FormFields.TYPE]:
        practiceAssessmentDetails?.practice_assessment_type ?? '',
      [FormFields.PRACTICE_ASSESSMENT]:
        practiceAssessmentDetails?.practice_assessment_uid ?? '',
    },
  });

  const onSubmit: SubmitHandler<FormData> = async ({ practiceAssessment }) => {
    const practiceUid =
      watch(FormFields.TYPE) && practiceAssessment ? practiceAssessment : '';
    await updateAssessmentPracticeAssessment(
      {
        interviewUid,
        practiceUid,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['getPracticeAssessmentDetails', interviewUid],
          });
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
          showSuccessNotification(
            t('Practice saved'),
            t('Your practice was successfully saved')
          );
        },
        onError: (error) => {
          showErrorNotification(
            t('An error occurred while updating assessment'),
            error.message
          );
        },
      }
    );
  };

  const currentType = watch(FormFields.TYPE);

  useEffect(() => {
    if (practiceAssessments?.length) {
      const typesFromServer: SelectMenuOption[] = practiceAssessments.map(
        (item) => ({
          value: item.id,
          primaryText: item.name,
        })
      );
      setTypes([
        {
          value: '',
          primaryText: t('--------------'),
        },
        ...typesFromServer,
      ]);
    }
  }, [practiceAssessments]);

  useEffect(() => {
    if (practiceAssessments?.length && currentType) {
      const assessmentsFromServer: SelectMenuOption[] =
        practiceAssessments
          .find((item) => item.id === currentType)
          ?.assessments.map((item) => ({
            value: item.uid,
            primaryText: item.name,
            secondaryText: `${item.video_questions} ${
              item.video_questions === 1 ? t('video') : t('videos')
            } · ${item.written_questions} ${t('written')}`,
          })) || [];
      setAssessments([...assessmentsFromServer].reverse());
    }
  }, [practiceAssessments, currentType]);

  useEffect(() => {
    if (watch(FormFields.TYPE) === '') {
      setValue(FormFields.PRACTICE_ASSESSMENT, '');
    }
  }, [watch(FormFields.TYPE)]);

  return (
    <ContentWrapper title={t('Practice')}>
      <Panel panelClassName="mt-6">
        {isLoading || isLoadingPracticeAssessmentDetails ? (
          <Skeleton height={80} />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <fieldset disabled={false}>
              <div className="flex flex-col lg:flex-row lg:items-end">
                <div className="lg:w-96">
                  <p className="prose-body-md-med">{t('Practice Type')}</p>
                  <p className="prose-body-sm-reg text-kira-slate-700">
                    {t('Select a default Kira practice or a custom practice.')}
                  </p>
                </div>
                <div className="lg:pl-12">
                  <Controller
                    control={control}
                    name={FormFields.TYPE}
                    render={({ field }) => (
                      <SelectMenu
                        options={types}
                        emptyText={t('Select type')}
                        value={
                          types.find(
                            (option) => option.value === field.value
                          ) || null
                        }
                        onChange={(option) => field.onChange(option.value)}
                        dataQa="practice-type-select"
                      />
                    )}
                  />
                </div>
              </div>

              {currentType && (
                <div className="mt-6 flex flex-col lg:flex-row lg:items-end">
                  <div className="border-l-4 border-kira-slate-200 pl-6 lg:w-96">
                    <p className="prose-body-md-med">{t('Practice')}</p>
                    <p className="prose-body-sm-reg text-kira-slate-700">
                      {t(
                        "Select a practice that applicants can use to familiarize themselves with the platform. We recommend selecting a practice with all questions types they'll see in their assessment."
                      )}
                    </p>
                  </div>
                  <div className="lg:pl-12">
                    <Controller
                      control={control}
                      name={FormFields.PRACTICE_ASSESSMENT}
                      render={({ field }) => (
                        <SelectMenu
                          options={assessments}
                          emptyText={t('Select practice')}
                          value={
                            assessments.find(
                              (option) => option.value === field.value
                            ) || null
                          }
                          onChange={(option) => field.onChange(option.value)}
                          dataQa="practice-assessment-select"
                        />
                      )}
                    />

                    <FormFieldError
                      message={
                        errors[FormFields.PRACTICE_ASSESSMENT]?.message ?? ''
                      }
                      isVisible={!!errors[FormFields.PRACTICE_ASSESSMENT]}
                      className="mt-2"
                    />
                  </div>
                </div>
              )}

              {isDirty && (
                <FormControls
                  isLoading={isLoadingForm}
                  onCancel={() => reset()}
                />
              )}
            </fieldset>
          </form>
        )}
      </Panel>
    </ContentWrapper>
  );
};

export default PracticeAssessment;
